import React, { useState } from 'react';
import { StatsCard } from 'common';
import useDatatable, {
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { SECONDARY_API_CLIENT } from 'services/API';
import { When } from 'UI/components/atoms';
import DataTable from 'UI/components/organisms/DataTable';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import { RowActions } from 'UI/constants/defaults';

import { CandidateQuickEntryDrawer } from '../CandidateQuickEntryDrawer';

import {
  COLUMNS,
  COPIES,
  MOCKED_DATA,
  ORDER_BY_OPTIONS,
  PERSISTENCE_KEY
} from './CandidateQuickEntryTable.constants';

export const CandidateQuickEntryTable = () => {
  const [uiState, setUiState] = useState({
    isDeleteDialogOpen: false,
    isDeleting: false,
    selectedCandidateQuickEntryId: null
  });

  const initialPreferences = getColumnPreferences(PERSISTENCE_KEY, 0, ORDER_BY_OPTIONS, COLUMNS);

  const {
    columnPreferences,
    count,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState
  } = useDatatable({
    apiInstance: SECONDARY_API_CLIENT,
    columnsDefinitions: COLUMNS,
    // endpoint: ENDPOINTS.quickEntries, WIP: apply the endpoints when it's ready
    initialPreferences,
    key: PERSISTENCE_KEY,
    orderByOptions: ORDER_BY_OPTIONS,
    shouldScrollOnNavigate: true
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;
  const { columns: finalColumns, columnOrder, orderBy, direction } = columnPreferences;

  const handleOpenDeleteDialog = ({ rowData }) => {
    if (!rowData) return;
    const item = extractObjectFromDataTable(COLUMNS, ['id'], rowData);

    setUiState(prev => ({
      ...prev,
      isDeleteDialogOpen: true,
      draft: item
    }));
  };

  const handleDeleteQuickEntry = () => console.log('WIP - Delete Quick Entry.');

  const handleEditQuickEntry = async ({ rowData }) => {
    if (!rowData) return;

    const { id: rowId } = extractObjectFromDataTable(COLUMNS, ['id'], rowData);

    setUiState(prev => ({
      ...prev,
      selectedCandidateQuickEntryId: rowId
    }));
  };

  const actionsButton = buildRowActionsRendererDefinition({
    actions: [
      { name: RowActions.Edit, onClick: handleEditQuickEntry },
      { name: RowActions.Delete, onClick: handleOpenDeleteDialog }
    ],
    columns: COLUMNS
  });

  const columnsToRender = getColumnsToRender([...finalColumns, actionsButton]);

  const closeDrawer = () => {
    setUiState(prev => ({
      ...prev,
      selectedCandidateQuickEntryId: null
    }));
  };

  return (
    <StatsCard header={COPIES.title} paddingContent={0}>
      <DataTable
        columnOrder={columnOrder?.length > 0 ? columnOrder : undefined}
        columns={columnsToRender}
        components={{ TableToolbar: () => null }}
        count={count ?? 0}
        data={MOCKED_DATA}
        draggableColumns={{ enabled: true }}
        elevation={0}
        loading={showWholeSkeleton}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnSortChange={handleColumnSortChange}
        onPageClick={handlePageChange}
        onPerPageClick={handlePerPageChange}
        onSearchTextChange={handleKeywordChange}
        onToggleFilters={handleFiltersToggle}
        page={page}
        refreshing={isLoading}
        rowsPerPage={perPage}
        searchText={keyword}
        selectToolbarPlacement="none"
        selectableRows="none"
        sortOrder={{ name: orderBy, direction }}
        subtitle={COPIES.emptyState.subtitle}
        title={COPIES.emptyState.title}
      />

      <When condition={uiState.isDeleteDialogOpen}>
        <DecisionDialog
          fetching={uiState.isDeleting}
          isOpened
          message={COPIES.deleteDialog.message}
          onClose={() => setUiState(prev => ({ ...prev, isDeleteDialogOpen: false }))}
          onConfirm={handleDeleteQuickEntry}
          showSeverityIcon
          title={COPIES.deleteDialog.title}
          withButtons="YesNo"
        />
      </When>

      <When condition={!!uiState.selectedCandidateQuickEntryId}>
        <CandidateQuickEntryDrawer
          onClose={closeDrawer}
          quickEntryId={uiState.selectedCandidateQuickEntryId}
        />
      </When>
    </StatsCard>
  );
};
